import React, { useState, Suspense } from "react";
import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Collapse,
  Paper,
  useTheme,
  CircularProgress,
  Divider
} from "@mui/material";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const PythonCodeEditor = React.lazy(() => import("./PythonCodeEditor"));

export default function PredictOutput({
  code,
  language = "python",
  options = [],
  correctAnswer = "",
  explanation = ""
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [selectedOption, setSelectedOption] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const normalize = (s) =>
    s.trim().toLowerCase().replace(/\\n/g, "\n");

  const handleSubmit = () => {
    if (!selectedOption) return;

    setIsCorrect(
      normalize(selectedOption) === normalize(correctAnswer)
    );

    setSubmitted(true);
  };

  const handleReset = () => {
    setSelectedOption("");
    setSubmitted(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 920,
        mx: "auto",
        mt: 3,
        mb: 4,
      }}
    >
      {/* Icon */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #ab47bc, #7b1fa2)",
          color: "#fff",
          mb: 1.5,
        }}
      >
        <Typography variant="body2" fontWeight="bold">?</Typography>
      </Box>

      {/* Instruction */}
      <Typography
        variant="body1"
        color="text.primary"
        fontWeight={500}
        sx={{ mb: 2 }}
      >
        Read the code below and choose the correct output.
      </Typography>

      {/* Code Area */}
      <Suspense
        fallback={
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        }
      >
        <Box sx={{ mb: 3 }}>
          <PythonCodeEditor
            initialCode={code}
            runnable={false}
            editorMinHeight="auto"
          />
        </Box>
      </Suspense>

      {/* Options Header */}
      <Typography
        variant="subtitle2"
        fontWeight={600}
        color="text.primary"
        sx={{ mb: 1.5 }}
      >
        Choose the correct output
      </Typography>

      {/* Options */}
      <FormControl disabled={submitted} sx={{ width: "100%" }}>
        <RadioGroup
          value={selectedOption}
          onChange={(e) => setSelectedOption(e.target.value)}
        >
          {options.map((option, index) => {
            const isSelected = selectedOption === option;
            return (
              <Box
                key={index}
                onClick={() => {
                  if (!submitted) setSelectedOption(option);
                }}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  width: "100%",
                  mb: 1.25,
                  py: 1.5,
                  px: 2.25,
                  borderRadius: 2,
                  cursor: submitted ? "default" : "pointer",
                  border: `1px solid ${
                    isSelected
                      ? theme.palette.primary.main
                      : isDark
                      ? "rgba(255,255,255,0.12)"
                      : "rgba(0,0,0,0.12)"
                  }`,
                  borderLeft: isSelected
                    ? `4px solid ${theme.palette.primary.main}`
                    : `1px solid ${
                        isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"
                      }`,
                  backgroundColor: isSelected
                    ? isDark
                      ? "rgba(25,118,210,0.08)"
                      : "rgba(25,118,210,0.04)"
                    : "transparent",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: !submitted
                      ? isDark
                        ? "rgba(255,255,255,0.04)"
                        : "rgba(0,0,0,0.02)"
                      : undefined,
                    borderColor:
                      !submitted && !isSelected
                        ? isDark
                          ? "rgba(255,255,255,0.2)"
                          : "rgba(0,0,0,0.2)"
                        : undefined,
                  },
                }}
              >
                <Radio
                  value={option}
                  checked={isSelected}
                  sx={{ p: 0, mr: 1.5, mt: "2px" }}
                />
                <Typography
                  component="pre"
                  sx={{
                    m: 0,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "'Consolas', 'Monaco', monospace",
                    fontSize: "0.95rem",
                    lineHeight: 1.6,
                    color: "text.primary",
                    flex: 1,
                  }}
                >
                  {option}
                </Typography>
              </Box>
            );
          })}
        </RadioGroup>
      </FormControl>

      {/* Button */}
      <Box sx={{ mt: 3, mb: 3, display: "flex" }}>
        {!submitted ? (
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedOption}
            onClick={handleSubmit}
            disableElevation
            sx={{
              width: { xs: "100%", sm: "auto" },
              px: 4,
              py: 1,
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Check Answer
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={handleReset}
            sx={{
              width: { xs: "100%", sm: "auto" },
              px: 4,
              py: 1,
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Try Again
          </Button>
        )}
      </Box>

      {/* Feedback */}
      <Collapse in={submitted}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            p: 2,
            borderRadius: 2,
            border: `1px solid ${isCorrect ? "#81c784" : "#e57373"}`,
            backgroundColor: isCorrect
              ? isDark
                ? "rgba(46,125,50,0.1)"
                : "rgba(232,245,233,0.5)"
              : isDark
              ? "rgba(198,40,40,0.1)"
              : "rgba(255,235,235,0.5)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {isCorrect ? (
              <CheckCircleIcon color="success" fontSize="small" />
            ) : (
              <CancelIcon color="error" fontSize="small" />
            )}
            <Typography
              variant="subtitle2"
              fontWeight={700}
              color={isCorrect ? "success.main" : "error.main"}
            >
              {isCorrect ? "Correct" : "Incorrect"}
            </Typography>
          </Box>
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ lineHeight: 1.6 }}
          >
            {explanation}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}